import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Resilient one-shot bootstrap with bounded retry + focus self-heal (#7202).
 *
 * Motivation: the auth-context user-data fetch used to latch after a SINGLE
 * attempt — a ref was flipped `true` *before* the fetch ran, so a transient or
 * partial failure (e.g. a 5xx wave on load) pinned a degraded state (an
 * advocate's Moja kancelária / Spisy sidebar completely missing) with no
 * self-heal until the user stumbled onto a manual refresh.
 *
 * This hook fixes the class of bug: it only latches on a *fully-successful*
 * attempt, retries transient failures with exponential backoff, and re-attempts
 * when the user returns to the tab (window focus) as long as bootstrap has not
 * yet succeeded — so a flaky first load recovers on its own.
 *
 * `attempt()` MUST resolve `true` only when the underlying work definitively
 * succeeded, and `false` for a transient failure that should be retried.
 * (Definitive "empty" states — e.g. a 404 that will never change on retry —
 * should resolve `true`, not `false`, so the hook does not spin uselessly.)
 */

/** Default retry budget: the initial attempt plus 3 backed-off retries. */
const BOOTSTRAP_MAX_ATTEMPTS = 4;
/** Base backoff; delays are 1.5s → 3s → 6s before the budget is exhausted. */
const BOOTSTRAP_BASE_DELAY_MS = 1500;

/**
 * Exponential backoff for the next retry. `failedAttempt` is the 1-based count
 * of the attempt that just failed. Returns the delay in ms before the next
 * attempt, or `null` once the budget is exhausted (caller should stop retrying
 * and wait for an external trigger, e.g. window focus).
 */
export function nextBootstrapRetryDelayMs(
  failedAttempt: number,
  maxAttempts: number,
  baseDelayMs: number
): number | null {
  if (failedAttempt >= maxAttempts) return null;
  return baseDelayMs * 2 ** (failedAttempt - 1);
}

interface UseResilientBootstrapOptions {
  /** Gate: run the bootstrap only while true (e.g. signed in with a user id). */
  enabled: boolean;
  /**
   * The work to run. Resolve `true` on definitive success, `false` on a
   * transient failure to retry. Its identity may change across renders — the
   * hook always calls the latest one.
   */
  attempt: () => Promise<boolean>;
  maxAttempts?: number;
  baseDelayMs?: number;
}

/**
 * @returns `hasSucceeded` — true once an attempt has fully succeeded for the
 * current `enabled` session. Resets to false when `enabled` goes false.
 */
export function useResilientBootstrap({
  enabled,
  attempt,
  maxAttempts = BOOTSTRAP_MAX_ATTEMPTS,
  baseDelayMs = BOOTSTRAP_BASE_DELAY_MS,
}: UseResilientBootstrapOptions): { hasSucceeded: boolean } {
  const [hasSucceeded, setHasSucceeded] = useState(false);

  // Latest-attempt ref: a queued retry or a focus handler must call the current
  // `attempt` closure, not the (potentially stale) one captured when scheduled.
  const attemptRef = useRef(attempt);
  useEffect(() => {
    attemptRef.current = attempt;
  }, [attempt]);

  // Mutable bootstrap state kept in a ref so the retry loop and event handlers
  // read/write it synchronously without re-render churn.
  const stateRef = useRef({
    started: false,
    succeeded: false,
    running: false,
    attempts: 0,
    // Bumped on every session reset (`enabled` → false). A `run()` captures the
    // epoch at start; when it resolves it drops its result if the epoch moved,
    // so an in-flight attempt from a signed-out session can't write into (or
    // block) the next session.
    epoch: 0,
    timer: null as ReturnType<typeof setTimeout> | null,
  });

  const clearTimer = useCallback(() => {
    const s = stateRef.current;
    if (s.timer) {
      clearTimeout(s.timer);
      s.timer = null;
    }
  }, []);

  const run = useCallback(async () => {
    const s = stateRef.current;
    if (s.running || s.succeeded) return; // serialize; never re-run after success
    clearTimer();
    const myEpoch = s.epoch;
    s.running = true;
    let ok = false;
    try {
      ok = await attemptRef.current();
    } catch {
      // A rejected attempt is treated as a transient failure (same as a
      // resolved `false`) so the backoff retry loop still runs. Without this
      // catch a throw would escape `run()` — no `attempts` increment, no timer
      // scheduled — silently stalling the bootstrap in a degraded state, the
      // exact latch class this hook exists to prevent (#7202). Callers should
      // still prefer resolving `false`, but a throw must never kill the loop.
      ok = false;
    } finally {
      // Only clear `running` if this run still owns the current epoch — a run
      // superseded by a session reset must not clobber the new session's flag.
      if (s.epoch === myEpoch) s.running = false;
    }
    // Superseded by a reset (e.g. sign-out) while awaiting — drop the result.
    if (s.epoch !== myEpoch) return;
    if (ok) {
      s.succeeded = true;
      s.attempts = 0;
      setHasSucceeded(true);
      return;
    }
    s.attempts += 1;
    const delay = nextBootstrapRetryDelayMs(
      s.attempts,
      maxAttempts,
      baseDelayMs
    );
    if (delay === null) return; // budget exhausted — wait for a focus-driven heal
    s.timer = setTimeout(() => {
      s.timer = null;
      void run();
    }, delay);
  }, [clearTimer, maxAttempts, baseDelayMs]);

  // Kick off once per enabled session; reset on disable (sign-out) so a later
  // sign-in bootstraps cleanly.
  useEffect(() => {
    const s = stateRef.current;
    if (!enabled) {
      clearTimer();
      // Invalidate any in-flight run and free the serialization flag so the
      // next enabled session can bootstrap even if the previous attempt is
      // still pending (#7202 sign-out/sign-in re-entrancy).
      s.epoch += 1;
      s.running = false;
      s.started = false;
      s.succeeded = false;
      s.attempts = 0;
      setHasSucceeded(false);
      return;
    }
    if (!s.started) {
      s.started = true;
      void run();
    }
  }, [enabled, run, clearTimer]);

  // Self-heal a degraded first load: re-attempt when the user returns to the
  // tab, as long as bootstrap hasn't fully succeeded yet.
  useEffect(() => {
    if (!enabled) return;
    const onFocus = () => {
      const s = stateRef.current;
      if (!s.succeeded && !s.running) {
        s.attempts = 0; // fresh retry budget on an explicit user return
        void run();
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [enabled, run]);

  // Clear any pending retry on unmount.
  useEffect(() => clearTimer, [clearTimer]);

  return { hasSucceeded };
}
